import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';

import * as approvalV2Api from '../../lib/api/approvalV2';
import * as taxApi from '../../lib/api/taxbill';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import ApprovalLineTemplateModal from '../../components/approval/ApprovalLineTemplateModal';
import ApprovalDelegationModal from '../../components/approval/ApprovalDelegationModal';
import ApprovalFinalAuthModal from '../../components/approval/ApprovalFinalAuthModal';

const DOC_TYPE_LABELS = { ORDER_BOOK: '수주품의서', HR_LEAVE: '휴가' };
const SCOPE_LABELS = { FINAL: '전결(최종승인)', NOTIFIER: '결과 통보' };
// 결재선 템플릿 LINE_SCOPE 라벨(이슈 #7, 2026-10-21). 목록에는 본인 템플릿뿐 아니라
// 부서/전체로 공개된 다른 사용자의 템플릿도 함께 뜬다(백엔드 가시성 필터 참고) - 수정/삭제는
// 작성자만 가능하므로 아래 테이블에서 소유 여부로 아이콘을 가린다.
const LINE_SCOPE_LABELS = { PRIVATE: '개인', DEPARTMENT: '부서', COMPANY: '전체', SYSTEM: '시스템' };

/**
 * 환경설정 > 결재 설정. ksm_frontend의 결재선 템플릿/대결 관리/전결 권한 3개 탭(ApprovalMngPage
 * 안의 lines/delegations/auths 라우트)을 한 화면으로 모았다(2026-10-20 결재 V2 이관) -
 * bito 규모에서는 탭마다 별도 라우트를 두지 않고 하나의 설정 화면으로 충분하다고 판단.
 */
const ApprovalSettingsContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));

  const [tab, setTab] = useState('lines');

  const [lines, setLines] = useState([]);
  const [lineModalOpen, setLineModalOpen] = useState(false);
  const [editingLine, setEditingLine] = useState(null);

  const [delegations, setDelegations] = useState([]);
  const [delegationModalOpen, setDelegationModalOpen] = useState(false);
  const [editingDelegation, setEditingDelegation] = useState(null);

  const [canManageAuth, setCanManageAuth] = useState(false);
  const [auths, setAuths] = useState([]);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [editingAuth, setEditingAuth] = useState(null);

  // 세금계산서 발행 요청 결재(스위치), 2026-10-21. KSM은 이 탭 자체가 없다(스위치 미적용).
  const [taxApprovalEnabled, setTaxApprovalEnabled] = useState(false);
  const [taxApprovalCanManage, setTaxApprovalCanManage] = useState(false);
  const [taxApprovalBusy, setTaxApprovalBusy] = useState(false);

  const loadTaxApproval = () => {
    taxApi.getIssueFlowConfig().then((res) => {
      setTaxApprovalEnabled(Boolean(res?.data?.approvalEnabled));
      setTaxApprovalCanManage(Boolean(res?.data?.canManage));
    });
  };

  const loadLines = () => {
    approvalV2Api.getApprovalV2Lines({}).then((res) => setLines(res?.data?.list || []));
  };

  const loadDelegations = () => {
    approvalV2Api.getApprovalV2Delegations().then((res) => setDelegations(res?.data?.list || []));
  };

  const loadAuths = () => {
    approvalV2Api.getApprovalV2AuthManagePermission().then((res) => {
      const can = Boolean(res?.data?.canManage);
      setCanManageAuth(can);
      if (can) {
        approvalV2Api.getApprovalV2Auths({}).then((r) => setAuths(r?.data?.list || []));
      }
    });
  };

  useEffect(() => {
    loadLines();
    loadDelegations();
    loadAuths();
    loadTaxApproval();
  }, []);

  const handleToggleTaxApproval = async (event) => {
    const enabled = event.target.checked;
    setTaxApprovalBusy(true);
    try {
      await taxApi.putIssueFlowConfig(enabled);
      setTaxApprovalEnabled(enabled);
      enqueueSnackbar(
        enabled
          ? '세금계산서 발행 요청 결재를 사용하도록 설정했습니다.'
          : '세금계산서 발행 요청 결재를 사용하지 않도록 설정했습니다.',
        { variant: 'success' },
      );
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    } finally {
      setTaxApprovalBusy(false);
    }
  };

  const handleSaveLine = async (payload) => {
    try {
      if (editingLine) {
        await approvalV2Api.editApprovalV2Line(editingLine.lineId, payload);
      } else {
        await approvalV2Api.addApprovalV2Line(payload);
      }
      enqueueSnackbar('결재선 템플릿을 저장했습니다.', { variant: 'success' });
      setLineModalOpen(false);
      setEditingLine(null);
      loadLines();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    }
  };

  const handleRemoveLine = async (line) => {
    try {
      await approvalV2Api.removeApprovalV2Line(line.lineId);
      enqueueSnackbar('결재선 템플릿을 삭제했습니다.', { variant: 'success' });
      loadLines();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    }
  };

  const handleSaveDelegation = async (payload) => {
    try {
      if (editingDelegation) {
        await approvalV2Api.editApprovalV2Delegation(editingDelegation.delegationId, payload);
      } else {
        await approvalV2Api.addApprovalV2Delegation(payload);
      }
      enqueueSnackbar('대결 설정을 저장했습니다.', { variant: 'success' });
      setDelegationModalOpen(false);
      setEditingDelegation(null);
      loadDelegations();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    }
  };

  const handleRemoveDelegation = async (delegation) => {
    try {
      await approvalV2Api.removeApprovalV2Delegation(delegation.delegationId);
      enqueueSnackbar('대결 설정을 삭제했습니다.', { variant: 'success' });
      loadDelegations();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    }
  };

  const handleSaveAuth = async (payload) => {
    try {
      if (editingAuth) {
        await approvalV2Api.editApprovalV2Auth(editingAuth.authId, payload);
      } else {
        await approvalV2Api.addApprovalV2Auth(payload);
      }
      enqueueSnackbar('전결 권한을 저장했습니다.', { variant: 'success' });
      setAuthModalOpen(false);
      setEditingAuth(null);
      loadAuths();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    }
  };

  const handleRemoveAuth = async (auth) => {
    try {
      await approvalV2Api.removeApprovalV2Auth(auth.authId);
      enqueueSnackbar('전결 권한을 삭제했습니다.', { variant: 'success' });
      loadAuths();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err.message, { variant: 'error' });
    }
  };

  return (
    <>
      <BreadcrumbsContainer title="환경설정/결재 설정" />
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={taxApprovalEnabled}
              disabled={!taxApprovalCanManage || taxApprovalBusy}
              onChange={handleToggleTaxApproval}
            />
          }
          label="세금계산서 발행 요청 결재 사용"
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          켜면 세금계산서 발행 요청이 결재(결재선 템플릿) 승인을 거쳐야 발행완료를 진행할 수
          있습니다. 끄면 기존처럼 요청 → 완료 2단계로 바로 처리됩니다.
          {!taxApprovalCanManage && ' (관리자만 변경할 수 있습니다)'}
        </Typography>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="lines" label="결재선 템플릿" />
          <Tab value="delegations" label="대결(위임) 관리" />
          {canManageAuth && <Tab value="auths" label="전결 권한" />}
        </Tabs>

        {tab === 'lines' && (
          <Box>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              sx={{ mb: 1 }}
              onClick={() => {
                setEditingLine(null);
                setLineModalOpen(true);
              }}
            >
              템플릿 추가
            </Button>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>템플릿명</TableCell>
                  <TableCell>범위</TableCell>
                  <TableCell>대상문서</TableCell>
                  <TableCell>단계</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="right">관리</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => {
                  const isOwner = line.crtUsr === user?.usrUuid;
                  return (
                    <TableRow key={line.lineId}>
                      <TableCell>
                        {line.lineNm}
                        {!isOwner && (
                          <Chip size="small" variant="outlined" label="공유됨" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell>{LINE_SCOPE_LABELS[line.lineScope] || line.lineScope}</TableCell>
                      <TableCell>{DOC_TYPE_LABELS[line.docType] || line.docType || '전체'}</TableCell>
                      <TableCell>{line.stepCount}단계</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={line.useYn === 'Y' ? '사용' : '미사용'}
                          color={line.useYn === 'Y' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {isOwner ? (
                          <>
                            <IconButton
                              size="small"
                              onClick={async () => {
                                const res = await approvalV2Api.getApprovalV2Line(line.lineId);
                                setEditingLine(res?.data?.info);
                                setLineModalOpen(true);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleRemoveLine(line)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            작성자만 수정 가능
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        등록된 결재선 템플릿이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {tab === 'delegations' && (
          <Box>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              sx={{ mb: 1 }}
              onClick={() => {
                setEditingDelegation(null);
                setDelegationModalOpen(true);
              }}
            >
              대결 설정 추가
            </Button>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>대결자</TableCell>
                  <TableCell>대상문서</TableCell>
                  <TableCell>기간</TableCell>
                  <TableCell>사유</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="right">관리</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {delegations.map((d) => (
                  <TableRow key={d.delegationId}>
                    <TableCell>{d.delegateUsrNm}</TableCell>
                    <TableCell>{DOC_TYPE_LABELS[d.docType] || d.docType || '전체'}</TableCell>
                    <TableCell>{d.strtDt} ~ {d.endDt}</TableCell>
                    <TableCell>{d.reasonTyp}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={d.activeYn === 'Y' ? '활성' : '비활성'}
                        color={d.activeYn === 'Y' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingDelegation(d);
                          setDelegationModalOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveDelegation(d)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {delegations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        등록된 대결 설정이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}

        {tab === 'auths' && canManageAuth && (
          <Box>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              sx={{ mb: 1 }}
              onClick={() => {
                setEditingAuth(null);
                setAuthModalOpen(true);
              }}
            >
              전결 권한 추가
            </Button>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>대상</TableCell>
                  <TableCell>대상문서</TableCell>
                  <TableCell>구분</TableCell>
                  <TableCell>금액 범위</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="right">관리</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auths.map((a) => (
                  <TableRow key={a.authId}>
                    <TableCell>{a.usrNm}</TableCell>
                    <TableCell>{DOC_TYPE_LABELS[a.docType] || a.docType}</TableCell>
                    <TableCell>{SCOPE_LABELS[a.authScope] || a.authScope}</TableCell>
                    <TableCell>
                      {a.minAmount ?? 0} ~ {a.maxAmount ?? '제한없음'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={a.activeYn === 'Y' ? '활성' : '비활성'}
                        color={a.activeYn === 'Y' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingAuth(a);
                          setAuthModalOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleRemoveAuth(a)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {auths.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        등록된 전결 권한이 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <ApprovalLineTemplateModal
        open={lineModalOpen}
        line={editingLine}
        currentUserUuid={user?.usrUuid}
        onClose={() => {
          setLineModalOpen(false);
          setEditingLine(null);
        }}
        onSave={handleSaveLine}
      />
      <ApprovalDelegationModal
        open={delegationModalOpen}
        delegation={editingDelegation}
        currentUserUuid={user?.usrUuid}
        onClose={() => {
          setDelegationModalOpen(false);
          setEditingDelegation(null);
        }}
        onSave={handleSaveDelegation}
      />
      <ApprovalFinalAuthModal
        open={authModalOpen}
        auth={editingAuth}
        onClose={() => {
          setAuthModalOpen(false);
          setEditingAuth(null);
        }}
        onSave={handleSaveAuth}
      />
    </>
  );
};

export default ApprovalSettingsContainer;
